<script setup lang="ts">
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import { ContentEditor } from '~/components/ui/content'
import { useDateFormat } from '~/composables/useDateTimeFormat'
import { LucideCalendar, LucideTag } from 'lucide-vue-next'
import type { PolicyDocument } from '~/composables/usePrivacyPolicy'

const store = usePrivacyPolicyModalStore()
const { t } = useI18n()
const { fetchPolicy } = usePrivacyPolicy()

const doc = ref<PolicyDocument | null>(null)
const isLoading = ref(false)
const hasReadToEnd = ref(false)
const sentinelEl = ref<HTMLElement | null>(null)
const isMounted = ref(false)

let observer: IntersectionObserver | null = null

onMounted(() => {
  isMounted.value = true
})

const setupObserver = () => {
  if (observer) observer.disconnect()
  if (!sentinelEl.value) return
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) hasReadToEnd.value = true
    },
    { threshold: 0.1 }
  )
  observer.observe(sentinelEl.value)
}

watch(
  () => store.isOpen,
  async (val) => {
    if (!val) {
      observer?.disconnect()
      return
    }
    hasReadToEnd.value = false
    isLoading.value = true
    try {
      doc.value = store.preloadedDoc ?? (await fetchPolicy())
      await nextTick()
      if (isMounted.value) setupObserver()
    } finally {
      isLoading.value = false
    }
  }
)

onUnmounted(() => {
  isMounted.value = false
  observer?.disconnect()
})

const agree = () => {
  if (!doc.value) return
  store.agree(doc.value.id)
}
</script>

<template>
  <Dialog :open="store.isOpen" @update:open="store.close()">
    <!-- Raised above other dialogs: it opens on top of the login modal after sign-in -->
    <DialogContent
      class="z-[60] flex h-full max-h-screen w-full flex-col gap-0 rounded-none p-0 sm:h-auto sm:max-h-[90vh] sm:max-w-[min(64rem,100%)] sm:rounded-lg"
      overlay-class="z-[60]"
    >
      <div class="border-b px-6 py-4">
        <DialogTitle>{{ doc?.name ?? t('form.register.privacy_modal_title') }}</DialogTitle>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-4 text-sm leading-relaxed text-gray-700">
        <div v-if="isLoading" class="animate-pulse space-y-3">
          <div class="h-6 w-2/3 rounded bg-gray-200" />
          <div class="h-4 w-full rounded bg-gray-200" />
          <div class="h-4 w-full rounded bg-gray-200" />
          <div class="h-4 w-5/6 rounded bg-gray-200" />
          <div class="mt-4 h-5 w-1/3 rounded bg-gray-200" />
          <div class="h-4 w-full rounded bg-gray-200" />
          <div class="h-4 w-full rounded bg-gray-200" />
          <div class="h-4 w-4/5 rounded bg-gray-200" />
          <div class="mt-4 h-5 w-1/3 rounded bg-gray-200" />
          <div class="h-4 w-full rounded bg-gray-200" />
          <div class="h-4 w-full rounded bg-gray-200" />
          <div class="h-4 w-3/4 rounded bg-gray-200" />
        </div>
        <ContentEditor v-else-if="doc" :html="doc.text" />
        <div ref="sentinelEl" />
      </div>

      <div class="border-t px-6 py-4">
        <div v-if="doc" class="mb-3 flex items-center justify-center gap-4 text-xs text-gray-500 sm:justify-start [&_svg]:size-3.5">
          <div class="flex items-center gap-1">
            <LucideCalendar />
            <span>{{ t('form.register.privacy_modal_updated') }} {{ useDateFormat(doc.updated_at) }}</span>
          </div>
          <div class="flex items-center gap-1">
            <LucideTag />
            <span>{{ t('form.register.privacy_modal_version') }} {{ doc.version }}</span>
          </div>
        </div>
        <p v-if="!hasReadToEnd" class="mb-3 text-center text-xs text-gray-400">
          {{ t('form.register.privacy_modal_read_to_end') }}
        </p>
        <div class="flex gap-3">
          <Button
            v-if="!store.closeable"
            variant="outline-blue"
            class="flex-1"
            @click="store.decline()"
          >
            {{ t('form.register.privacy_modal_decline') }}
          </Button>
          <Button class="flex-1" :disabled="!hasReadToEnd" @click="agree">
            {{ t('form.register.privacy_modal_agree') }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
